/** Checked transport for the agreed dated-lease API, pending generated-schema cutover. */
import { apiFetch } from "./client";
import { Refused, refusalFrom } from "./refusal";

export type LeaseType = "unknown" | "owned" | "finance_lease" | "operating_lease" | "occupied_under_licence";
export interface LeaseRevision {
  id: string; org_id: string; facility_id: string; lease_type: LeaseType;
  operates: boolean | null; effective_from: string | null; answered_by: string | null;
  reason: string; created_at: string; supersedes_id: string | null;
  superseded_at: string | null; inherited: boolean;
}
export interface LeaseHistory { facility_id: string; history: LeaseRevision[] }
export interface LeaseCommand {
  intent: "correct" | "change"; expected_revision_id: string; effective_from: string | null;
  lease_type: Exclude<LeaseType, "unknown">; operates: boolean | null; basis: string;
}
export interface LeaseArtifactImpact {
  artifact_id: string; obligation_id: string; payload_moved: boolean; newly_divergent: boolean;
  already_divergent: boolean; stored_intact: boolean; matches_today: boolean; format_changed: boolean;
}
export interface LeasePreview {
  preview_binding: string; not_changed: boolean; changed: string[];
  affected_from: string | null; affected_to: string | null; moved_obligation_ids: string[];
  affected_artifacts: LeaseArtifactImpact[]; invalidated_requests_count: number;
  requires_report_review: boolean;
}
export interface LeaseSaved extends LeasePreview { revision_id: string | null; history: LeaseRevision[] }

function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function bad(): never { throw new Error("The lease correction response could not be read. Reload the history before continuing."); }
function row(value: unknown): Record<string, unknown> { if (!object(value)) return bad(); return value; }
function text(value: unknown): string { if (typeof value !== "string") return bad(); return value; }
function flag(value: unknown): boolean { if (typeof value !== "boolean") return bad(); return value; }
function id(value: unknown): string {
  const result = text(value);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(result)) return bad();
  return result;
}
function day(value: unknown): string {
  const result = text(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(result) || Number.isNaN(Date.parse(result))
    || new Date(result).toISOString().slice(0, 10) !== result) return bad();
  return result;
}
function leaseTimestamp(value: unknown): string {
  const result = text(value);
  if (!/^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/.test(result) || Number.isNaN(Date.parse(result))) return bad();
  return result;
}
function nullable<T>(value: unknown, read: (value: unknown) => T): T | null { return value === null ? null : read(value); }
function list<T>(value: unknown, read: (value: unknown) => T): T[] { if (!Array.isArray(value)) return bad(); return value.map(read); }
function lease(value: unknown): LeaseType {
  switch (value) {
    case "unknown": case "owned": case "finance_lease": case "operating_lease": case "occupied_under_licence": return value;
    default: return bad();
  }
}
function revision(value: unknown): LeaseRevision {
  const r = row(value);
  return { id: id(r.id), org_id: id(r.org_id), facility_id: id(r.facility_id), lease_type: lease(r.lease_type),
    operates: nullable(r.operates, flag), effective_from: nullable(r.effective_from, day),
    answered_by: nullable(r.answered_by, id), reason: text(r.reason), created_at: leaseTimestamp(r.created_at),
    supersedes_id: nullable(r.supersedes_id, id), superseded_at: nullable(r.superseded_at, leaseTimestamp), inherited: flag(r.inherited) };
}
export function readLeaseHistory(value: unknown): LeaseHistory {
  const r = row(value);
  const facilityId = id(r.facility_id);
  const history = list(r.history, revision);
  if (history.some((version) => version.facility_id !== facilityId)
    || new Set(history.map((version) => version.id)).size !== history.length
    || new Set(history.map((version) => version.org_id)).size > 1) return bad();
  return { facility_id: facilityId, history };
}
export function readLeasePreview(value: unknown): LeasePreview {
  const r = row(value);
  const binding = text(r.preview_binding);
  if (!/^[0-9a-f]{64}$/.test(binding) || !Number.isSafeInteger(r.invalidated_requests_count) || typeof r.invalidated_requests_count !== "number"
    || r.invalidated_requests_count < 0) return bad();
  return { preview_binding: binding, not_changed: flag(r.not_changed), changed: list(r.changed, text),
    affected_from: nullable(r.affected_from, day), affected_to: nullable(r.affected_to, day),
    moved_obligation_ids: list(r.moved_obligation_ids, id),
    affected_artifacts: list(r.affected_artifacts, (value) => {
      const a = row(value);
      return { artifact_id: id(a.artifact_id), obligation_id: id(a.obligation_id), payload_moved: flag(a.payload_moved),
        newly_divergent: flag(a.newly_divergent), already_divergent: flag(a.already_divergent), stored_intact: flag(a.stored_intact),
        matches_today: flag(a.matches_today), format_changed: flag(a.format_changed) };
    }), invalidated_requests_count: r.invalidated_requests_count, requires_report_review: flag(r.requires_report_review) };
}
export function readLeaseSaved(value: unknown): LeaseSaved {
  const r = row(value);
  return { ...readLeasePreview(r), revision_id: nullable(r.revision_id, id), history: list(r.history, revision) };
}
function path(facilityId: string, action: string) { return `/api/v1/facilities/${encodeURIComponent(id(facilityId))}/lease/${action}`; }
async function request<T>(url: string, decode: (value: unknown) => T, body?: unknown, saving = false): Promise<T> {
  const response = await apiFetch(url, body === undefined ? {} : {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const value: unknown = await response.json().catch(() => null);
  if (!response.ok) throw refusalFrom(response, value, "The lease correction request could not be completed.");
  try { return decode(value); } catch (error) {
    if (saving) throw new Refused(0, "lease_save_unreadable", "The save response could not be read. Reload the version history to check whether the correction was saved.");
    throw error;
  }
}
export async function getLeaseHistory(facilityId: string): Promise<LeaseHistory> {
  const result = await request(path(facilityId, "history"), readLeaseHistory);
  if (result.facility_id !== facilityId) return bad();
  return result;
}
export function previewLeaseCorrection(facilityId: string, command: LeaseCommand): Promise<LeasePreview> {
  return request(path(facilityId, "preview"), readLeasePreview, command);
}
export function saveLeaseCorrection(facilityId: string, command: LeaseCommand, previewBinding: string): Promise<LeaseSaved> {
  return request(path(facilityId, "corrections"), (value) => {
    const saved = readLeaseSaved(value);
    readLeaseHistory({ facility_id: facilityId, history: saved.history });
    if (saved.revision_id !== null && !saved.history.some((version) => version.id === saved.revision_id)) return bad();
    return saved;
  }, { ...command, preview_binding: previewBinding, confirmed: true }, true);
}
